import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { axiosInstance } from '../../utils/axios';
import './doctor.scss';

/**
 * 医師初回セットアップ画面
 * 業務用LINE LIFFからアクセス
 */
const DoctorOnboardingPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [onboardingStatus, setOnboardingStatus] = useState(null);

  // フォームデータ
  const [formData, setFormData] = useState({
    last_name: '',
    first_name: '',
    email: '',
    phone_number: '',
    online_training_certificate: null,
    insurance_doctor_certificate: null,
  });

  // ファイルプレビュー用
  const [filePreview, setFilePreview] = useState({
    online_training_certificate: null,
    insurance_doctor_certificate: null,
  });

  // 初回セットアップステータス確認
  useEffect(() => {
    const checkStatus = async () => {
      try {
        const response = await axiosInstance.get('/api/medichat_doctor/onboarding/status');
        setOnboardingStatus(response.data);
        
        // 既に完了している場合はプロフィールページへ
        if (response.data.onboarding_completed) {
          navigate('/doctor/profile');
          return;
        }
        
        // 既存データがあれば反映
        if (response.data.doctor) {
          const doc = response.data.doctor;
          setFormData(prev => ({
            ...prev,
            last_name: doc.last_name || '',
            first_name: doc.first_name || '',
            email: doc.email || '',
            phone_number: doc.phone_number || '',
          }));
        }
      } catch (err) {
        console.error('[Onboarding] ステータス確認エラー:', err);
        setError('ステータスの確認に失敗しました');
      } finally {
        setLoading(false);
      }
    };
    
    checkStatus();
  }, [navigate]);

  // 入力変更ハンドラ
  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  }, []);

  // ファイル選択ハンドラ
  const handleFileChange = useCallback((e) => {
    const { name, files } = e.target;
    if (files && files[0]) {
      setFormData(prev => ({ ...prev, [name]: files[0] }));
      
      // プレビュー用URL生成
      const reader = new FileReader();
      reader.onloadend = () => {
        setFilePreview(prev => ({ ...prev, [name]: reader.result }));
      };
      reader.readAsDataURL(files[0]);
    }
  }, []);

  // フォーム送信
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      // バリデーション
      if (!formData.last_name || !formData.first_name) {
        throw new Error('氏名を入力してください');
      }
      if (!formData.email) {
        throw new Error('メールアドレスを入力してください');
      }
      if (!formData.phone_number) {
        throw new Error('電話番号を入力してください');
      }

      // FormDataで送信（ファイルアップロードのため）
      const submitData = new FormData();
      submitData.append('last_name', formData.last_name);
      submitData.append('first_name', formData.first_name);
      submitData.append('email', formData.email);
      submitData.append('phone_number', formData.phone_number);
      
      if (formData.online_training_certificate) {
        submitData.append('online_training_certificate', formData.online_training_certificate);
      }
      if (formData.insurance_doctor_certificate) {
        submitData.append('insurance_doctor_certificate', formData.insurance_doctor_certificate);
      }

      const response = await axiosInstance.post('/api/medichat_doctor/onboarding/complete', submitData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (response.data.success) {
        // 完了画面へ
        navigate('/doctor/setup/complete');
      } else {
        throw new Error(response.data.error || '登録に失敗しました');
      }
    } catch (err) {
      console.error('[Onboarding] 登録エラー:', err);
      setError(err.response?.data?.error || err.message || '登録に失敗しました');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="doctor-onboarding-page">
        <div className="loading-container">
          <div className="loader"></div>
          <p>読み込み中...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="doctor-onboarding-page">
      <div className="onboarding-header">
        <div className="logo">👨‍⚕️ CureNow</div>
        <h1>業務開始に向けた雇用登録の設定</h1>
        <p className="subtitle">以下の情報を入力してください。運営による確認後、稼働可能になります。</p>
      </div>

      {error && (
        <div className="error-banner">
          <span className="error-icon">⚠️</span>
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="onboarding-form">
        {/* 氏名 */}
        <div className="form-section">
          <h3>👤 氏名（正式名称）</h3>
          <div className="name-row">
            <div className="form-group">
              <label htmlFor="last_name">姓 <span className="required">*</span></label>
              <input
                type="text"
                id="last_name"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                placeholder="山田"
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="first_name">名 <span className="required">*</span></label>
              <input
                type="text"
                id="first_name"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                placeholder="太郎"
                required
              />
            </div>
          </div>
        </div>

        {/* メールアドレス */}
        <div className="form-section">
          <h3>📧 メールアドレス</h3>
          <div className="form-group">
            <label htmlFor="email">
              メールアドレス <span className="required">*</span>
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="example@email.com"
              required
            />
            <p className="form-hint">PC管理画面のログインに使用します</p>
          </div>
        </div>

        {/* 電話番号 */}
        <div className="form-section">
          <h3>📞 電話番号</h3>
          <div className="form-group">
            <label htmlFor="phone_number">
              電話番号 <span className="required">*</span>
            </label>
            <input
              type="tel"
              id="phone_number"
              name="phone_number"
              value={formData.phone_number}
              onChange={handleChange}
              placeholder="090-1234-5678"
              required
            />
          </div>
        </div>

        {/* オンライン診療研修修了証 */}
        <div className="form-section">
          <h3>📜 オンライン診療研修修了証</h3>
          <div className="form-group">
            <label>
              研修修了証の画像
            </label>
            <div className="file-upload-area">
              <input
                type="file"
                id="online_training_certificate"
                name="online_training_certificate"
                accept="image/*,.pdf"
                onChange={handleFileChange}
                className="file-input"
              />
              <label htmlFor="online_training_certificate" className="file-label">
                {filePreview.online_training_certificate ? (
                  <div className="preview-container">
                    <img src={filePreview.online_training_certificate} alt="プレビュー" className="file-preview" />
                    <span className="change-text">タップして変更</span>
                  </div>
                ) : (
                  <div className="upload-placeholder">
                    <span className="upload-icon">📎</span>
                    <span>ファイルを選択</span>
                  </div>
                )}
              </label>
            </div>
            <p className="form-hint">※後からアップロードすることも可能です</p>
          </div>
        </div>

        {/* 保険医登録証 */}
        <div className="form-section">
          <h3>🏥 保険医登録証</h3>
          <div className="form-group">
            <label>
              保険医登録証の画像
            </label>
            <div className="file-upload-area">
              <input
                type="file"
                id="insurance_doctor_certificate"
                name="insurance_doctor_certificate"
                accept="image/*,.pdf"
                onChange={handleFileChange}
                className="file-input"
              />
              <label htmlFor="insurance_doctor_certificate" className="file-label">
                {filePreview.insurance_doctor_certificate ? (
                  <div className="preview-container">
                    <img src={filePreview.insurance_doctor_certificate} alt="プレビュー" className="file-preview" />
                    <span className="change-text">タップして変更</span>
                  </div>
                ) : (
                  <div className="upload-placeholder">
                    <span className="upload-icon">📎</span>
                    <span>ファイルを選択</span>
                  </div>
                )}
              </label>
            </div>
            <p className="form-hint">※後からアップロードすることも可能です</p>
          </div>
        </div>

        {/* 送信ボタン */}
        <div className="form-actions">
          <button
            type="submit"
            className="submit-button"
            disabled={submitting}
          >
            {submitting ? '送信中...' : '登録する'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default DoctorOnboardingPage;
